import { useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import type { GridSortModel } from "@mui/x-data-grid"

import type { SxProps, Theme } from "@mui/material"

import type { Powerline } from "types/powerlines.types"

import DataGridTable from "widgets/common/DataGridTable"
import SkeletonWrapper from "widgets/common/SkeletonWrapper"
import {
  getPowerlinesTableColumns,
  getPowerlinesTableRows,
  POWERLINES_TABLE_CSV_FIELDS,
} from "helpers/utils/powerlines"

import {
  selectPowerlinesTable,
  setPowerlinesTableRowsAmount,
  setPowerlinesTableSortModel,
} from "features/store/tablesSlice"
import useRoutes from "helpers/hooks/useRoutes"
import { formatFilename } from "helpers/utils/common"
import { selectCurrentOrgName } from "features/store/orgSlice"

interface PowerlinesTableProps {
  powerlines: Powerline[]
  isLoading: boolean
  sx?: SxProps<Theme>
}

const PowerlinesTable = ({ powerlines, isLoading, sx = {} }: PowerlinesTableProps) => {
  const { baseOrgURL } = useRoutes()
  const { i18n, t } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  const powerlinesTable = useSelector(selectPowerlinesTable)
  const orgName = useSelector(selectCurrentOrgName)
  const dispatch = useDispatch()

  const columns = useMemo(
    () => getPowerlinesTableColumns(baseOrgURL, t, locale),
    [baseOrgURL, t, locale],
  )
  const rows = useMemo(
    () => getPowerlinesTableRows(powerlines, locale),
    [powerlines, locale],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => dispatch(setPowerlinesTableSortModel(model)),
    [dispatch],
  )

  return (
    <SkeletonWrapper isWaiting={isLoading || !powerlines} height={540} width={"100vw"}>
      <DataGridTable
        sx={sx}
        rows={rows}
        columns={columns}
        rowLines={2}
        csvFileName={formatFilename(`${orgName}_${t("powerlines.POWERLINES")}`, "csv")}
        csvFields={POWERLINES_TABLE_CSV_FIELDS}
        gridProps={{ columnVisibilityModel: { id: false, name: false } }}
        sortModel={powerlinesTable.sortModel}
        onSortModelChange={handleSortModelChange}
        pageSize={powerlinesTable.qtyRows}
        onPageSizeChange={(pageSize) => dispatch(setPowerlinesTableRowsAmount(pageSize))}
      />
    </SkeletonWrapper>
  )
}

export default PowerlinesTable
